import React, { useState, useEffect } from 'react';
import { Box, Divider, Flex, useToast } from "@chakra-ui/react";

import { MdDashboardCustomize } from 'react-icons/md';
import WarningTextMessage from '../../../global/components/helper-components/components/WarningTextMessage';
import FormTextInput from '../../../forms/TextInputField/components/FormTextInput';
import appConfig from '../../../../config/app-config';
import LaunchdeckButton from '../../../global/components/helper-components/components/LaunchdeckButton';
import ReactIconDisplay from '../../../global/components/helper-components/components/ReactIconDisplay';
import FormTextAreaInput from '../../../forms/TextareaField/components/FormTextAreaInput';
import { FaFileCircleCheck } from 'react-icons/fa6';
import LaunchdeckCloseButton from '../../../global/components/helper-components/components/LaunchdeckCloseButton';
import DsrGenericLoadingModal from '../../../digital-sales-room/components/DsrGenericLoadingModal';
import { triggerLaunchdeckToast } from '../../../global/helpers/global-helpers';
import { IoIosArrowDroprightCircle } from 'react-icons/io';



export function CreateRoomWithAiForm({
  showLoader,
  isMobileView,
  handleIsUploadingState,
  handleClickModalCloseButton,
  handleWaitTillRedirect,
  changeDsrPopupState,
  changeDsrModalState,
  uploadFiles,
  uploadFileMultithreaded,
  uploadFilesReset,
  createGenAiRoom,
  setNotifyGenAiRoomReady,
  // Redux state
  uploadFilesState
}) {

  const instructionsCharacterLimit = 300;
  const toast = useToast();
  const [showIsGeneratingRoom, setShowIsGeneratingRoom] = useState(false)

  const {
    maxVideoFileSize,
    minVideoFileSizeForMultithreadedUpload,
    displayMaxFileSize,
  } = appConfig;


  const [isLoading, setIsLoading] = useState("")
  const [instructions, setInstructions] = useState("")
  const [isEmptyInstructions, setIsEmptyInstructions] = useState("")
  const [isEmptyDsrTitle, setIsEmptyDsrTitle] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [dsrTitleDisplay, setDsrTitleDisplay] = useState("")
  const [selectedFile, setSelectedFile] = useState("")
  const [uploadResults, setUploadResults] = useState("")
  const [uploadedFileSpecs, setUploadedFileSpecs] = useState(null)

  const [notifyLater, setNotifyLater]= useState(false)


  const handleSwitchToUserCreation = () => {
    changeDsrPopupState('create-new-room-form')
    changeDsrModalState(true)
  }

  const setShowNextLoadingState = () => {
    setShowIsGeneratingRoom(true)
  }

  const handleNotifyLater = () => {
    setNotifyLater(true)
    setNotifyGenAiRoomReady(true)
    changeDsrPopupState('')
    changeDsrModalState(false)
  }

  useEffect(() => {
    if (notifyLater === true) {
      toast(
        triggerLaunchdeckToast({
          useCase: "show-success-state",
          label: "We'll let you know in a few minutes when the Room's ready",
          isMobileView
        })
      )
      
      handleClickModalCloseButton()
    }
  }, [notifyLater])

  useEffect(() => {
    if (uploadFilesState) {
      const { isLoading: isUploading, results: uploadResults, error: uploadingError } = uploadFilesState;
   
      if (typeof isUploading === "boolean") {
        setIsLoading(isUploading)
      }

      if (uploadingError) {
        setErrorMessage(uploadingError)
      }

      if (uploadResults) {
        setUploadResults(uploadResults)
        uploadResults?.files && setShowNextLoadingState()
      }
    }
  }, [uploadFilesState])

  useEffect(() => {
    // Send the data of the uploaded file to generate room
    if (!isLoading &&
      !errorMessage &&
      Array.isArray(uploadResults?.files)) {

      setUploadedFileSpecs(uploadResults.files[0])
      uploadFilesReset();

    }
  }, [uploadResults])

  useEffect(() => {
    if (uploadedFileSpecs && instructions) {
      createGenAiRoom({
        dsrTitle: dsrTitleDisplay,
        instructions: instructions,
        referenceFile: uploadedFileSpecs
      })
      setUploadedFileSpecs(null)
    }
  }, [uploadedFileSpecs, instructions])

  const handleFileInputChange = (e) => {
    const file = e?.target?.files[0];

    const validateInput = (file) => {
      if (!file) {
        throw new Error("UploadedVideo must be object");
      }

      if (!file?.name) {
        throw new Error("UploadedVideo does not have filename");
      }

      if (file?.size > maxVideoFileSize) {
        // File size exceeds the limit
        setErrorMessage(
          `We support the display of files that are ${displayMaxFileSize / (1024 * 1024)} MB or below.`,
        );
        throw new Error("File size exceeds the limit");
      }
    };

    try {
      validateInput(file);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.log(e);

      return;
    }
    setSelectedFile(file);
  };

  const onChangeHandler = (event) => {
    switch (event.target.name) {
      case ('instructions'): {
        setInstructions(event.target.value)
        break;
      }
      case ('dsrTitle'): {
        setDsrTitleDisplay(event.target.value)
        break;
      }
      default: {
        break;
      }
    }
  }

  const handleButtonClick = () => {
    if (!instructions.trim()) {
      setIsEmptyInstructions(true)
    } else {
      setIsEmptyInstructions(false)
    }

    if (!dsrTitleDisplay.trim()) {
      setIsEmptyDsrTitle(true)
    } else {
      setIsEmptyDsrTitle(false)
    }

    if (dsrTitleDisplay.trim() && instructions.trim()) {
      if (!!selectedFile) {
        selectedFile?.size <= minVideoFileSizeForMultithreadedUpload
          ? uploadFiles({ files: [selectedFile] })
          : uploadFileMultithreaded({ file: selectedFile });

          handleWaitTillRedirect()
      }

      handleIsUploadingState()
    }
  }

  if (showLoader) {
    return (
    <DsrGenericLoadingModal
      triggerStateChange={showIsGeneratingRoom}
      modalText={showIsGeneratingRoom ? `Got it & generating your Room now...\n It'll be ready in a minute or two.` : `Give us a moment... \nWe're working on understanding the information provided`} 
      handleOkOption = {() => showIsGeneratingRoom && handleNotifyLater()}
      />

)
  } else {

    return (
      <Box m='1.5em' w="100%" >
        <Flex mb='1em' bgGradient="linear(to-r, #2D3748, #243b55)" rounded='0.25em' p='0.5em'>
          <Flex gap={4} p='0.1em' w='100%' justify='space-between' align='center' cursor='pointer' onClick={() => handleSwitchToUserCreation()}>
            <Flex gap={4}>
              <ReactIconDisplay reactIcon={MdDashboardCustomize} color='white' fontSize='1em' mr='0em' />
              <Box fontSize='0.75em' color='white'>
                Have a solid vision for your Room & prefer to tailor it personally?
              </Box>
            </Flex>
            <ReactIconDisplay reactIcon={IoIosArrowDroprightCircle} color='white' fontSize='1em' mr='0em' />
          </Flex>
        </Flex>

        <Box my='1em'>
          <FormTextInput name="dsrTitle" htmlFor='dsrTitle' label="Room name" isRequiredField={true} placeholder="" mb='0.2em' value={dsrTitleDisplay} onChange={onChangeHandler} />
          <WarningTextMessage errorLogic={isEmptyDsrTitle} message={`Give your Room a good name`} />
        </Box>

        <Box my='1em'>
          <FormTextAreaInput
            label="Highlight which key topics?"
            name="instructions"
            fontSize='0.875em'
            isRequiredField={true}
            currentValue={instructions}
            charLimit={instructionsCharacterLimit}
            onChange={onChangeHandler} />
          <WarningTextMessage errorLogic={isEmptyInstructions} message={`Please guide us with some basic instructions on how you want your page to be. \n For example - my aim is to get them to understand my product and services better to book a meeting with me`} />
        </Box>

        <Box my='1em'>
          <Box fontSize="0.875em" >Reference material</Box>
          <Box fontSize='0.7em' color='gray.500' mb={4}>We currently support PDF docs. More to come!</Box>
          {selectedFile ?

            <Flex w='100%' h='5em' border='1px solid' borderColor='gray.300' justify='space-between' align='center' rounded='0.25em' p='1em'>

              <Flex justify='flex-start' align='center' gap={4} flex='auto'>
                <Box as={FaFileCircleCheck} boxSize="1.5em" />
                <Box>
                  <Box color='brandDark.500' fontSize='0.75em' fontStyle='italic' className='truncate-long-text'>
                    {selectedFile?.name ? selectedFile?.name : "File"}
                  </Box>
                  <Box color='gray.500' fontSize='0.7em'>
                    PDF
                  </Box>
                </Box>


              </Flex>
              <Box w='1.5em'>
                <LaunchdeckCloseButton handleCloseModal={() => setSelectedFile(null)} />
              </Box>

            </Flex> :
            <React.Fragment>
              <label>
                <input
                  style={{ display: "none" }}
                  type="file"
                  accept=".pdf, .ppt, .pptx"
                  onChange={handleFileInputChange}
                />
                <LaunchdeckButton
                  bg="brandDark.500"
                  color="white"
                  px="2em"
                  label="Select"
                />
              </label>
            </React.Fragment>
          }
        </Box>

        <Divider />

        <Flex direction='column' mt='1em' justify='center' alignItems='center'>
          <Flex justify={['center', 'flex-end', 'flex-end', 'flex-end']} w='100%'>
            <LaunchdeckButton bg='brand.500' color='white' onClick={() => handleButtonClick()} label='Start Room' />
          </Flex>
        </Flex>
      </Box>
    )
  }
}
